/**
 * Section Revert Hook
 *
 * Provides functionality to revert a section to its previous content (before AI edit).
 * Handles both the backend PATCH and Redux state cleanup.
 */

import { useCallback } from 'react';
import { useUpdateNoteSectionMutation } from '@features/notes';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  clearAffectedSection,
  clearSectionSnapshot,
  finalizeSectionUpdate,
} from '../store/chatSlice';
import { selectSectionPreviousContent } from '../store/selectors';

export interface UseSectionRevertParams {
  /** The note ID */
  noteId: string;
}

export interface UseSectionRevertReturn {
  /** Function to revert a section to its previous content */
  revertSection: (sectionId: string, sectionIndex: number) => Promise<void>;
  /** Map of section IDs to their previous content */
  previousContent: Record<string, string>;
}

/**
 * Hook for reverting sections to their previous content (before AI edit)
 *
 * @param noteId - The note ID
 * @returns Object with revertSection function and previousContent map
 */
export const useSectionRevert = ({
  noteId,
}: UseSectionRevertParams): UseSectionRevertReturn => {
  const dispatch = useAppDispatch();
  const previousContent = useAppSelector(selectSectionPreviousContent);
  const [updateSection] = useUpdateNoteSectionMutation();

  const revertSection = useCallback(
    async (sectionId: string, sectionIndex: number) => {
      const previous = previousContent[sectionId];
      if (!previous) {
        console.warn(`No previous content found for section ${sectionId}`);
        return;
      }

      try {
        // PATCH backend with previous content
        await updateSection({
          noteId,
          sectionIndex,
          editSectionRequest: { edited_text: previous },
        }).unwrap();

        // Clear sectionUpdates (shows previous content), affected state, and snapshot
        dispatch(finalizeSectionUpdate(sectionId)); // Clear sectionUpdates
        dispatch(clearAffectedSection(sectionId));
        dispatch(clearSectionSnapshot(sectionId));
      } catch (error) {
        console.error('Failed to revert section:', error);
        throw error;
      }
    },
    [previousContent, updateSection, dispatch, noteId],
  );

  return { revertSection, previousContent };
};

